import type { WorkshopChip } from './workshopsCalendarTypes';

/** A sub-region a page can filter by, with the accent its workshops carry. */
export interface Subregion {
  label: string;
  color: string;
}

export interface SubregionFilter {
  /**
   * The sub-regions worth offering as filters, in first-seen order. Empty when
   * the workshops span fewer than two: one sub-region is not a choice.
   */
  subregions: Subregion[];
  /** The hidden labels that still match an option, so a stale label filters nothing. */
  hidden: Set<string>;
  /** The workshops left once the hidden sub-regions are taken out. */
  shown: WorkshopChip[];
}

/** The List's empty message while sub-regions are hidden: "nothing coming up" may be untrue. */
export const EMPTY_WITH_HIDDEN_SUBREGIONS = 'No upcoming workshops for the sub-regions shown.';

export function subregionsOf(workshops: WorkshopChip[]): Subregion[] {
  const seen = new Map<string, string>();
  for (const w of workshops) {
    if (w.subregionLabel !== undefined && !seen.has(w.subregionLabel)) {
      seen.set(w.subregionLabel, w.accentColor);
    }
  }
  return Array.from(seen, ([label, color]) => ({ label, color }));
}

export function toggleHiddenLabel(hidden: Set<string>, label: string): Set<string> {
  const next = new Set(hidden);
  if (next.has(label)) next.delete(label);
  else next.add(label);
  return next;
}

export function filterBySubregion(
  workshops: WorkshopChip[],
  hiddenLabels: Iterable<string>,
): SubregionFilter {
  const found = subregionsOf(workshops);
  // A lone sub-region has no filter to switch it back on, so it is never hidden.
  const subregions = found.length > 1 ? found : [];
  const known = new Set(subregions.map((s) => s.label));
  const hidden = new Set([...hiddenLabels].filter((label) => known.has(label)));
  const shown = workshops.filter(
    (w) => w.subregionLabel === undefined || !hidden.has(w.subregionLabel),
  );
  return { subregions, hidden, shown };
}
